import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/usjp-logo__1_-removebg-preview.png";
import { openPaymentGateway } from "../../services/cyberGatewayService";
import { checkRegistrationPaymentStatus } from "../../services/authService";
import { API_BASE } from "../../services/api";

// "Confirm Your Payment" — reached from a "Proceed to Payment" button after
// registration, or from the login-resume flow. The payee (member OR club) is
// carried in via props (modal mode) or router state (route mode) and MUST
// include the registration `process_id` and `payable_amount`; opening the route
// directly with no payee sends the user back to login.
//
// Rendered as a full-screen page (over a blurred view of whatever triggered
// it, in modal mode) with two payment methods:
//   • Card Payment — opens the CyberExt gateway with the real `payment_payload`
//     carried on the member/club object (from the registration response or, for
//     a resumed unpaid registration, /verify-otp's
//     registration_process.payment_payload). This dialog does not simulate
//     success: the registration process is only marked paid by the backend
//     once it receives the real gateway's payment confirmation. While the
//     popup is open, this polls the backend directly
//     (POST /api/registration-payments/status) every few seconds; the moment
//     that reports "Paid" it closes the popup itself (no gateway cooperation
//     needed — a window you opened can be closed even after it navigates
//     cross-origin) and redirects to /payment-success. While that poll is in
//     flight (gatewayOpened && no paymentNotice yet) the main button is
//     disabled and shows a spinner + "Confirming your payment..." so a
//     second click can't reopen the popup or fire a duplicate submission. If
//     the user closes the popup first (before a "Paid" status arrives), a
//     final check runs on close, and if it's still not paid the page stays
//     open with a pending/failed notice so the user can retry.
//   • Pay at Counter — no gateway involved. Shows the registration's existing
//     payment_reference plus instructions for paying in person; the process
//     stays "Pending" until staff confirm the cash payment on the backend.

const buildName = (initials, denoted, lastname) => {
  const lead = String(initials || denoted || "").trim();
  const tail = String(lastname || "").trim();
  return [lead, tail].filter(Boolean).join(" ") || "—";
};

// Visa wordmark rendered inline (no external asset / network request) so the
// card badge matches the brand without loading an image.
const VisaMark = () => (
  <span className="select-none font-bold italic tracking-tight text-[15px] leading-none" style={{ color: "#1a1f71" }}>
    VISA
  </span>
);

// Small inline "brand" badges for the accepted-card strip — text-only (no
// external logo assets available) but styled to read as recognizable marks.
const CardBrandBadge = ({ children, style }) => (
  <span
    className="inline-flex items-center justify-center h-7 px-2.5 rounded border border-gray-200 bg-white text-[10px] font-bold tracking-tight select-none"
    style={style}
  >
    {children}
  </span>
);

export default function PaymentMethod({
  member: memberProp,
  club: clubProp,
  email: emailProp,
  onClose,
  onSuccess,
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();

  // Two modes:
  //  • Modal — rendered on top of another page (e.g. RegistrationStatus or the
  //    registration "submitted" popups) with a `member` OR a `club` plus
  //    `onClose`/`onSuccess` callbacks. `onSuccess` is now superseded by the
  //    automatic redirect to /payment-success once payment is verified as
  //    "Paid" — it's kept for any other caller-side cleanup, but is no longer
  //    what decides whether the user reached a paid state.
  //  • Route — opened standalone at /payment-method, reading router state and
  //    navigating on close/success. (kept for backward compatibility; member only)
  const isModal = Boolean(memberProp || clubProp);
  const member = memberProp || location.state?.member || null;
  const club = clubProp || location.state?.club || null;
  // Club registration pays a club-level fee; everything else is a member fee.
  const isClub = Boolean(club);
  // The registration PROCESS id + payable amount are threaded in from the
  // registration response (on the member/club object). Payment now targets the
  // process, and the amount is known up-front — so there's no extra (admin-only)
  // payments lookup to make here.
  const processId = member?.process_id ?? club?.process_id ?? null;
  // Real gateway payload from the backend — exactly as returned, never rebuilt.
  const paymentPayload = member?.payment_payload ?? club?.payment_payload ?? null;
  // Used to ask the backend for this specific payment's real status afterwards.
  const paymentReference = member?.payment_reference ?? club?.payment_reference ?? null;
  // The "pay at counter" reference number: the same income code + reference
  // already prefixed server-side (see PaymentGatewayPayload::make's
  // `reference2` — income code followed by paymentReference) and sent as-is
  // to the payment gateway, so the counter staff see the identical number
  // the gateway would have used. Falls back to the bare paymentReference if
  // the payload is unavailable.
  const counterReferenceNumber = paymentPayload?.reference2 || paymentReference;
  // Printable "pay at counter" voucher PDF — a public, unauthenticated route
  // (RegistrationPaymentController::member/clubApplicantShroffVoucher) since
  // the applicant has no session/token at this point in the flow; identity is
  // proven with processId + payment_reference instead. The backend streams
  // the PDF with Content-Disposition: attachment, so a plain link triggers
  // the browser's native download — no fetch/blob handling needed.
  const voucherUrl =
    processId && paymentReference
      ? `${API_BASE}/api/registration-payments/${isClub ? "clubs" : "members"}/${processId}/applicant-shroff-voucher?payment_reference=${encodeURIComponent(paymentReference)}`
      : null;
  // The gateway-payment success reference shown on /payment-success: just
  // Reference 2 from the payload, not combined with the Income Code. Falls
  // back to the bare paymentReference if the payload is unavailable.
  const successReferenceNumber = paymentPayload?.reference2 || paymentReference;
  const email = emailProp || location.state?.email || member?.email || "";

  // "card" (gateway) or "counter" (pay in person). Card is the default/
  // recommended option.
  const [selected, setSelected] = useState("card");
  const [amount] = useState(() => {
    const a = member?.payable_amount ?? club?.payable_amount;
    return a != null ? Number(a) : null;
  });
  // Nonce that increments each time the gateway popup is (re)opened — truthy
  // once opened, used to relabel the button and re-enable a second click; it
  // does NOT mean payment succeeded. It's a counter rather than a boolean so
  // that "Reopen Payment Window" (which opens a fresh popup while this was
  // already truthy) still changes the value and re-triggers the polling
  // effect below, which is keyed on this same value.
  const [gatewayOpened, setGatewayOpened] = useState(0);
  const [error, setError] = useState("");
  // Handle to the gateway popup so we can detect when the user closes it
  // (`.closed` is readable cross-origin even though `.location`/content isn't).
  const popupRef = useRef(null);
  const [verifying, setVerifying] = useState(false);
  // Set once a verification call comes back with a non-"Paid" status — shown
  // as a pending/failed notice rather than treated as an error.
  const [paymentNotice, setPaymentNotice] = useState("");
  // Set once "Pay at Counter" has been confirmed — swaps the page over to the
  // reference-number/instructions view below.
  const [counterConfirmed, setCounterConfirmed] = useState(false);

  // Route mode only: page opened directly with no payee → back to login.
  useEffect(() => {
    if (!isModal && !member && !club) navigate("/login", { replace: true });
  }, [isModal, member, club, navigate]);

  const name = useMemo(
    () =>
      isClub
        ? club?.club_name || "Your Club"
        : buildName(member?.initials, member?.name_denoted_by_initials, member?.lastname),
    [isClub, club, member]
  );

  const registrationTypeLabel = isClub
    ? "Club Registration"
    : member?.member_type
    ? `${member.member_type} Registration`
    : "Membership Registration";

  const amountLabel = amount != null ? `LKR ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—";

  // Asks the backend whether this process/payment_reference has actually
  // cleared with the real gateway. On "Paid", redirect to the existing
  // /payment-success page; otherwise leave the user here with a notice.
  const verifyPayment = async () => {
    if (!processId || !paymentReference) return;
    setVerifying(true);
    setPaymentNotice("");
    try {
      console.log("[payment-status] request sent at", new Date().toISOString());
      const res = await checkRegistrationPaymentStatus({
        process_type: isClub ? "club" : "member",
        process_id: processId,
        payment_reference: paymentReference,
      });
      console.log("[payment-status] response received at", new Date().toISOString());
      if (res?.payment_status === "Paid") {
        navigate("/payment-success", {
          state: { amount, reference: successReferenceNumber, email, name },
        });
        return;
      }
      setPaymentNotice(
        res?.payment_status
          ? `Payment status: ${res.payment_status}. If you completed the payment, please wait a moment and check again.`
          : "Payment is still pending. If you completed the payment, please wait a moment and check again."
      );
    } catch {
      setPaymentNotice("Could not verify payment status. Please check again.");
    } finally {
      setVerifying(false);
    }
  };

  // While the gateway popup is open, poll the backend for the real payment
  // status every few seconds. The instant it reports "Paid" we close the
  // popup ourselves and go straight to /payment-success — the user never has
  // to close it by hand. If the user closes it first (payment abandoned or
  // the gateway's own success page doesn't self-close), fall back to a single
  // verifyPayment() check so a genuinely completed-but-not-yet-caught payment
  // still gets picked up.
  useEffect(() => {
    if (!gatewayOpened) return undefined;
    const popup = popupRef.current;
    if (!popup || !processId || !paymentReference) return undefined;

    let stopped = false;
    let inFlight = false;

    const stopPolling = () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };

    const tick = async () => {
      if (stopped || inFlight) return;
      if (popup.closed) {
        stopPolling();
        verifyPayment();
        return;
      }
      inFlight = true;
      try {
        console.log("[payment-status] request sent at", new Date().toISOString());
        const res = await checkRegistrationPaymentStatus({
          process_type: isClub ? "club" : "member",
          process_id: processId,
          payment_reference: paymentReference,
        });
        console.log("[payment-status] response received at", new Date().toISOString());
        if (stopped) return;
        if (res?.payment_status === "Paid") {
          stopPolling();
          popup.close();
          navigate("/payment-success", {
            state: { amount, reference: successReferenceNumber, email, name },
          });
        }
      } catch {
        // Transient error — keep polling; the close-on-manual-close and
        // final-check fallback still apply.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(tick, 3000);

    // Background tabs get their setInterval ticks heavily throttled by the
    // browser — and this tab becomes a background tab for as long as the
    // gateway popup holds focus (which can be minutes, across a declined
    // retry). Re-check immediately the moment the user comes back instead of
    // waiting on a throttled tick to eventually land.
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      tick();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayOpened]);

  const handleContinue = () => {
    if (!processId) {
      setError("Missing registration reference. Please restart your registration.");
      return;
    }
    setError("");
    setPaymentNotice("");

    if (selected === "counter") {
      // No gateway involved — just surface the existing reference number and
      // in-person instructions. The registration stays "Pending" until staff
      // confirm the cash payment on the backend.
      setCounterConfirmed(true);
      return;
    }

    if (!paymentPayload) {
      setError("Missing payment details. Please restart your registration.");
      return;
    }

    // Open the external payment gateway with the backend's real payment_payload,
    // passed through unchanged. Once the popup is closed, the effect above
    // checks the real payment status with the backend before deciding whether
    // to move on to the success page.
    const popup = openPaymentGateway(paymentPayload);
    if (!popup) {
      // window.open() was blocked by the browser — no popup exists to poll,
      // so leave gatewayOpened unchanged (don't increment it) and let the
      // user retry after allowing popups (see the polling effect's !popup
      // guard, which would otherwise silently never start).
      popupRef.current = null;
      setError("Your browser blocked the payment window. Please allow popups for this site and try again.");
      return;
    }
    popupRef.current = popup;
    setGatewayOpened((n) => n + 1);
  };

  if (!member && !club) return null;

  const handleCancel = () => {
    if (isModal) onClose?.();
    else navigate(-1);
  };

  // Shown full-screen the moment we start checking the real payment status —
  // whether triggered automatically (popup closed) or via the manual retry
  // button — so the user is never left staring at the page wondering whether
  // anything is happening.
  if (verifying) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 text-center max-w-xs">
          <svg className="animate-spin w-12 h-12 text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
          </svg>
          <p className="text-white text-lg font-semibold">Verifying your payment…</p>
          <p className="text-white/70 text-sm">Please wait while we confirm your payment.</p>
        </div>
      </div>
    );
  }

  // ── Header bar shared by both the main payment page and the "pay at
  // counter" confirmation page below ─────────────────────────────────────
  const HeaderBar = () => (
    <header className="flex items-center gap-4 px-5 sm:px-8 py-4" style={{ backgroundColor: "rgba(10,20,50,0.97)" }}>
      <div className="flex items-center gap-3">
        <img src={logo} alt="USJ Logo" className="w-10 h-10 sm:w-12 sm:h-12 object-contain flex-shrink-0" />
        <div>
          <p className="font-bold text-white text-sm sm:text-base leading-tight whitespace-nowrap">University of Sri Jayewardenepura</p>
          <p className="text-xs sm:text-sm leading-tight" style={{ color: "#e8a020" }}>Physical Education Unit</p>
        </div>
      </div>
    </header>
  );

  // ── "Pay at Counter" confirmation page — reference number + instructions ──
  if (counterConfirmed) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-blue-100">
        <HeaderBar />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <button
            onClick={() => setCounterConfirmed(false)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-blue-700 transition mb-4"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-9 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
              </svg>
            </div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">Pay at Counter</h1>
            <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
              Your registration is saved and pending payment. Take your reference number below to the club office / administration counter to complete payment.
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl px-5 py-5 mt-6 text-center">
              <p className="text-xs font-medium text-gray-500">Reference Number</p>
              <p className="text-2xl sm:text-3xl font-extrabold text-blue-800 mt-1 tracking-wide font-mono break-all">
                {counterReferenceNumber || "—"}
              </p>
              <p className="text-xs text-gray-500 mt-2">Amount due: <span className="font-semibold text-gray-700">{amountLabel}</span></p>
            </div>

            {voucherUrl && (
              <a
                href={voucherUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 border-2 border-blue-700 text-blue-700 hover:bg-blue-50 font-semibold py-3 rounded-xl text-sm mt-4 transition"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                </svg>
                Download Voucher
              </a>
            )}

            <div className="text-left bg-gray-50/80 rounded-xl p-5 mt-6">
              <h2 className="text-sm font-bold text-gray-900 mb-3">How to complete your payment</h2>
              <ol className="space-y-2.5 text-sm text-gray-600 list-decimal list-inside">
                <li>Visit the club office / administration counter at the Physical Education Unit.</li>
                <li>Give the counter staff your reference number shown above.</li>
                <li>Pay the exact amount due, <span className="font-semibold">{amountLabel}</span>, in cash.</li>
                <li>Your registration will be activated once the payment is confirmed.</li>
              </ol>
            </div>

            <p className="text-xs text-amber-600 mt-5 flex items-center justify-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Your registration stays pending until the counter payment is confirmed.
            </p>

            <button
              onClick={handleCancel}
              className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-semibold py-3 rounded-xl text-sm mt-7 transition shadow-sm hover:shadow-md"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cardSelected = selected === "card";
  const counterSelected = selected === "counter";
  // True from the moment the gateway popup is opened until either the
  // background poll (see the effect above) confirms "Paid" and navigates
  // away, or the popup is closed and comes back with a non-"Paid" notice
  // (at which point the retry button below takes over). Drives the
  // disabled/spinner state on the main button so a second click can't fire
  // while a confirmation is already in flight.
  const confirmingPayment = cardSelected && gatewayOpened && !paymentNotice;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <HeaderBar />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-3">
            <svg className="w-7 h-7 text-blue-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900">Confirm Your Payment</h1>
          <p className="text-sm text-gray-500 mt-1">
            Review your payment details and complete your {isClub ? "club registration" : "registration"}.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
          {/* Left: payment method selection */}
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-7">
            <h2 className="text-base font-bold text-gray-900">Select Payment Method</h2>
            <p className="text-sm text-gray-500 mt-0.5 mb-5">Choose how you would like to complete your payment.</p>

            {/* Card Payment option */}
            <label
              className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-4 cursor-pointer transition ${
                cardSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="paymentMethod"
                className="sr-only"
                checked={cardSelected}
                onChange={() => setSelected("card")}
              />
              <span
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  cardSelected ? "border-blue-600" : "border-gray-300"
                }`}
              >
                {cardSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </span>
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </span>
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-gray-900">Card Payment</span>
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">Pay securely using your debit or credit card</span>
              </span>
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex-shrink-0">
                Recommended
              </span>
            </label>

            {/* Pay at Counter option */}
            <label
              className={`relative flex items-start gap-3 rounded-xl border-2 px-4 py-4 cursor-pointer transition mt-3 ${
                counterSelected ? "border-blue-600 bg-blue-50/60" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="paymentMethod"
                className="sr-only"
                checked={counterSelected}
                onChange={() => setSelected("counter")}
              />
              <span
                className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  counterSelected ? "border-blue-600" : "border-gray-300"
                }`}
              >
                {counterSelected && <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
              </span>
              <span className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m6-14h.01M9 11h.01M9 15h.01M14 7h.01M14 11h.01M14 15h.01" />
                </svg>
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-sm font-bold text-gray-900">Pay at Counter</span>
                <span className="block text-xs text-gray-500 mt-0.5">Pay at the club office or administration counter</span>
              </span>
            </label>

            {/* Secure note */}
            <div className="flex items-start gap-2.5 rounded-xl bg-blue-50/60 px-4 py-3.5 mt-5">
              <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>
                <span className="block text-xs font-semibold text-gray-800">Secure Payment</span>
                <span className="block text-xs text-gray-500 mt-0.5">
                  Your payment information is encrypted and secure. We do not store your card details.
                </span>
              </span>
            </div>

            {error && <p className="text-sm text-red-500 text-center mt-4">{error}</p>}
            {paymentNotice && <p className="text-sm text-amber-600 text-center mt-4">{paymentNotice}</p>}

            <div className="border-t border-gray-100 mt-5 pt-5">
              <button
                onClick={handleContinue}
                disabled={confirmingPayment}
                className="w-full bg-blue-700 hover:bg-blue-800 active:bg-blue-900 text-white font-semibold py-3 rounded-xl text-sm transition shadow-sm hover:shadow-md disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-blue-700"
              >
                <span className="flex items-center justify-center gap-2">
                  {confirmingPayment ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Confirming Payment…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      {cardSelected && gatewayOpened ? "Reopen Payment Window" : "Proceed to Pay"}
                    </>
                  )}
                </span>
              </button>
              {confirmingPayment ? (
                <p className="text-xs font-medium text-amber-600 text-center mt-3">
                  Confirming your payment... Please do not close this page.
                </p>
              ) : (
                <p className="text-xs text-gray-400 text-center mt-3">
                  {cardSelected
                    ? "You will be redirected to a secure payment gateway"
                    : "You'll get a reference number to pay at the counter"}
                </p>
              )}
              {paymentNotice && (
                <button
                  onClick={verifyPayment}
                  className="w-full text-sm font-medium text-blue-700 hover:text-blue-800 border border-blue-100 hover:bg-blue-50 rounded-xl py-2.5 mt-3 transition"
                >
                  Check Payment Status Again
                </button>
              )}
              <button
                onClick={handleCancel}
                className="w-full text-sm font-medium text-gray-500 hover:text-gray-700 border border-gray-200 hover:bg-gray-50 rounded-xl py-3 mt-3 transition"
              >
                Cancel Payment
              </button>
            </div>
          </div>

          {/* Right: payment summary */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-7">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </span>
              <h2 className="text-base font-bold text-gray-900">Payment Summary</h2>
            </div>

            <dl className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-gray-500">Registration Type</dt>
                <dd className="font-medium text-gray-800 text-right truncate">{registrationTypeLabel}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-gray-500">{isClub ? "Club Name" : "Full Name"}</dt>
                <dd className="font-medium text-gray-800 text-right truncate">{name}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-gray-500">Contact Email</dt>
                <dd className="font-medium text-gray-800 text-right truncate">{email || "—"}</dd>
              </div>
            </dl>

            <div className="border-t border-gray-100 mt-4 pt-4 flex items-center justify-between gap-3 text-sm">
              <dt className="text-gray-500">Registration Fee</dt>
              <dd className="font-medium text-gray-800">{amountLabel}</dd>
            </div>

            <div className="border-t border-gray-100 mt-4 pt-4 flex items-center justify-between gap-3">
              <dt className="text-sm font-semibold text-gray-800">Total Amount</dt>
              <dd className="text-lg font-extrabold text-blue-700">{amountLabel}</dd>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/60 rounded-xl px-4 py-4 mt-5">
              <p className="text-xs font-medium text-gray-500">Amount to be paid</p>
              <p className="text-2xl font-extrabold text-blue-800 mt-0.5">{amountLabel}</p>
            </div>

            <div className="flex items-start gap-2.5 rounded-xl bg-green-50 px-4 py-3.5 mt-4">
              <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>
                <span className="block text-xs font-semibold text-green-700">100% Secure Payment</span>
                <span className="block text-xs text-green-700/80 mt-0.5">Your payment is protected by industry-standard encryption</span>
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mt-5">
              <span className="inline-flex items-center justify-center h-7 px-2.5 rounded border border-gray-200 bg-white">
                <VisaMark />
              </span>
              <CardBrandBadge style={{ color: "#eb001b" }}>MASTERCARD</CardBrandBadge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
